import { useRouter } from 'next/router'

import useHasura from '@/hooks/useHasura'
import usePagination from '@/hooks/usePagination'
import usePriceRange from '@/hooks/usePriceRange'
import Filters from '@/services/Filters/Filters'
import Aggregate from '@/ts/interfaces/global/Aggregate'
import ProductPrice from '@/ts/interfaces/product/ProductPrice'
import Product from '@/ts/Product'

import {
  GET_NEW_PRODUCTS_COUNT_QUERY,
  GET_NEW_PRODUCTS_HIGHEST_PRICE,
  GET_NEW_PRODUCTS_QUERY,
} from '../queries/products/getNewProducts'

function useNewProducts() {
  const router = useRouter()
  const currentFilters = Filters.get() || {}
  const priceRange = usePriceRange(100)
  const creator_nickname = router.query.creator

  const variables = {
    ...currentFilters,
    ...priceRange,
    creator_nickname,
  }

  const queryAggregate = useHasura<QueryAggregate>(
    'new_products_aggregate',
    GET_NEW_PRODUCTS_COUNT_QUERY,
    { variables }
  )

  const count = queryAggregate.data?.products_aggregate?.aggregate?.count || 0
  const { limit, offset } = usePagination(count)
  const config = { enabled: count > 0 }

  const queryProducts = useHasura<QueryProducts>(
    ['new_products', router.query.sort_by, router.query.direction],
    GET_NEW_PRODUCTS_QUERY,
    {
      config,
      variables: { ...variables, limit, offset },
    }
  )

  const queryPrice = useHasura<QueryPrice>(
    'new_products_highest_price',
    GET_NEW_PRODUCTS_HIGHEST_PRICE,
    { config }
  )

  const isLoading = queryProducts.isFetching && queryProducts.isPreviousData
  const products = queryProducts.data?.products || []
  const maxPrice = Number(queryPrice.data?.products?.[0]?.price?.value) || 0

  return { products, count, maxPrice, isLoading }
}

interface QueryProducts {
  products: Product[]
}

interface QueryPrice {
  products: {
    price: ProductPrice
  }[]
}

interface QueryAggregate {
  products_aggregate: Aggregate
}

export default useNewProducts
